import { useState, useEffect, useCallback } from "react";
import { toast } from "@jsq/ds/core";
import { sendChatMessage } from "../api/clients/chatClient";
import { listMessages, type ListedMessage } from "../api/clients/messageClient";
import { refreshUrl } from "../api/clients/urlClient";
import { isURL, isStringArray, findLastAssistantMessageIndex } from "../utils";

export type DisplayMessage = {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  isLoading?: boolean;
  thinkingText?: string;
  followupQuestions?: string[];
  feedback?: boolean | null;
  summary?: string;
  refreshFailed?: boolean;
};

export type UseChatMessagesParams = {
  conversationId: string | null;
  exploreId: string | null;
  modelName: string | null;
};

type UseChatMessagesReturn = {
  messages: DisplayMessage[];
  currentConversationId: string | null;
  handleSubmit: (prompt: string) => Promise<void>;
  isLoading: boolean;
  listMessagesLoading: boolean;
  updateMessageFeedback: (messageId: string, feedback: boolean | null) => void;
  lastValidUrl: string | undefined;
  isRefreshingUrl: boolean;
};

type MessageMetadata = Record<string, unknown> | null | undefined;

const hasFollowUpQuestions = (
  metadata: Record<string, unknown>,
): metadata is Record<string, unknown> & { follow_up_questions: string | string[] } => {
  return (
    "follow_up_questions" in metadata &&
    (typeof metadata.follow_up_questions === "string" ||
      isStringArray(metadata.follow_up_questions))
  );
};

const parseFollowupQuestions = (metadata: MessageMetadata): string[] | undefined => {
  if (!metadata) {
    return undefined;
  }

  let followupQuestions: string[] | undefined;

  if (hasFollowUpQuestions(metadata)) {
    const followupQuestionsData = metadata.follow_up_questions;

    try {
      let parsed: unknown;

      if (typeof followupQuestionsData === "string") {
        parsed = JSON.parse(followupQuestionsData);
      } else {
        parsed = followupQuestionsData;
      }

      if (isStringArray(parsed)) {
        followupQuestions = parsed;
      }
    } catch {
      return undefined;
    }
  }
  return followupQuestions;
};

export const useChatMessages = ({
  conversationId,
  exploreId,
  modelName,
}: UseChatMessagesParams): UseChatMessagesReturn => {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(
    conversationId,
  );
  const [isNewlyCreated, setIsNewlyCreated] = useState<boolean>(!conversationId);
  const [lastValidUrl, setLastValidUrl] = useState<string | undefined>(undefined);
  const [isRefreshingUrl, setIsRefreshingUrl] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listMessagesLoading, setListMessagesLoading] = useState<boolean>(!!conversationId);
  const [listedMessages, setListedMessages] = useState<ListedMessage[] | null>(null);

  const handleSubmit = useCallback(
    async (prompt: string) => {
      const timestamp = new Date().toISOString();
      // Temporary ID for optimistic UI update. Uses timestamp to ensure uniqueness
      // and "temp-" prefix for easy filtering when real message IDs arrive from server.
      // These temporary messages are replaced with server-generated IDs once the mutation completes.
      const userMessage: DisplayMessage = {
        id: `temp-user-${Date.now()}`,
        content: prompt,
        role: "user",
        createdAt: timestamp,
      };

      const loadingId = `temp-loading-${Date.now()}`;
      const loadingMessage: DisplayMessage = {
        id: loadingId,
        content: "",
        role: "assistant",
        createdAt: timestamp,
        isLoading: true,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage, loadingMessage]);
      setIsLoading(true);

      try {
        const stream = sendChatMessage({
          role: "user",
          content: prompt,
          metadata: {
            ...(currentConversationId && { conversation_id: currentConversationId }),
            // Only include exploreId and modelName if this is a new conversation.
            // For existing conversations the backend uses values from the stored conversation.
            ...(exploreId && !currentConversationId && { explore_name: exploreId }),
            ...(modelName && !currentConversationId && { model_name: modelName }),
          },
        });

        for await (const event of stream) {
          if (event.type === "progress") {
            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === loadingId ? { ...msg, thinkingText: event.content } : msg,
              ),
            );
            continue;
          }

          const { completion } = event;

          if (
            completion.conversationId &&
            completion.conversationId !== currentConversationId
          ) {
            setCurrentConversationId(completion.conversationId);
          }

          setMessages((prevMessages) => {
            const persistedMessages = prevMessages.filter(
              (msg) => !msg.isLoading && !msg.id.startsWith("temp-"),
            );

            const newMessages: DisplayMessage[] = [];
            if (completion.requestMessageObject) {
              const req = completion.requestMessageObject;
              newMessages.push({
                id: req.id,
                role: req.role,
                content: req.content,
                createdAt: req.createdAt,
              });
            }
            if (completion.responseMessageObject) {
              const res = completion.responseMessageObject;
              const followupQuestions = parseFollowupQuestions(res.metadata);
              newMessages.push({
                id: res.id,
                role: res.role,
                // Dashboard responses carry the embed URL on `completion.url` (resolved from
                // metadata.embed_url); `res.content` is LLM prose and won't pass isURL().
                content: completion.url ?? res.content,
                createdAt: res.createdAt,
                followupQuestions,
                summary: res.summary ?? undefined,
              });
            }

            const existingIds = new Set(persistedMessages.map((msg) => msg.id));
            const uniqueNewMessages = newMessages.filter((msg) => !existingIds.has(msg.id));

            return [...persistedMessages, ...uniqueNewMessages];
          });
        }
      } catch {
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => !msg.isLoading && !msg.id.startsWith("temp-")),
        );
        toast.error("Failed to send message. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [currentConversationId, exploreId, modelName],
  );

  // Reset messages when conversation changes
  useEffect(() => {
    setCurrentConversationId(conversationId);
    setMessages([]);
    setListedMessages(null);
    setIsNewlyCreated(!conversationId);
  }, [conversationId]);

  const handleRefreshUrl = useCallback(async (messageId: string) => {
    setIsRefreshingUrl(true);
    try {
      const result = await refreshUrl(messageId);
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                content: result.content,
                isLoading: false,
              }
            : msg,
        ),
      );
    } catch {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { ...msg, isLoading: false, refreshFailed: true } : msg,
        ),
      );
      toast.error("Failed to refresh dashboard. Please try again.");
    } finally {
      setIsRefreshingUrl(false);
    }
  }, []);

  const updateMessageFeedback = useCallback((messageId: string, feedback: boolean | null) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => (msg.id === messageId ? { ...msg, feedback } : msg)),
    );
  }, []);

  useEffect(() => {
    if (isNewlyCreated || !currentConversationId) {
      setListMessagesLoading(false);
      return;
    }

    let cancelled = false;
    setListMessagesLoading(true);

    listMessages(currentConversationId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setListedMessages(result.messages);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        toast.error("Failed to load conversation history.");
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setListMessagesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isNewlyCreated, currentConversationId]);

  useEffect(() => {
    if (!listedMessages) {
      return;
    }

    const displayMessages: DisplayMessage[] = listedMessages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      createdAt: msg.createdAt,
      feedback: msg.positiveFeedback,
      summary: msg.summary ?? "",
    }));
    const reversedMessages = [...displayMessages].reverse();

    const lastAssistantMessage = reversedMessages.findLast(
      (msg) => msg.role === "assistant" && isURL(msg.content),
    );

    if (lastAssistantMessage) {
      setMessages(
        reversedMessages.map((msg) =>
          msg.id === lastAssistantMessage.id
            ? {
                ...msg,
                isLoading: true,
              }
            : msg,
        ),
      );

      void handleRefreshUrl(lastAssistantMessage.id);
    } else {
      setMessages(reversedMessages);
    }
  }, [listedMessages, handleRefreshUrl]);

  useEffect(() => {
    const lastAssistantMessageIndex = findLastAssistantMessageIndex(messages, true);

    if (lastAssistantMessageIndex >= 0) {
      setLastValidUrl(messages[lastAssistantMessageIndex].content);
    }
  }, [messages]);

  return {
    messages,
    currentConversationId,
    handleSubmit,
    isLoading,
    listMessagesLoading,
    updateMessageFeedback,
    lastValidUrl,
    isRefreshingUrl,
  };
};
